var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { a as RestServiceBase, R as RestResult } from "../RestServiceBase-3b5c02c9.js";
import * as _ from "lodash";
import { cache } from "./PromiseCache.js";
import { i as isGuid } from "../CommonStringUtils-1580a6f6.js";
import MSALAuthService from "./MSALAuthService/MSALAuthService.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const resource = "https://graph.microsoft.com";
class GraphService extends RestServiceBase {
  constructor(baseAddress) {
    super({ url: baseAddress || "https://graph.microsoft.com/v1.0/", tokenScope: `${resource}/.default` });
  }
  /**
   * Returns the ids of groups that the user belongs to.
   * @param id the id of the user resource or group
   * @param groupIds the ids of the groups to check
   * @see https://msdn.microsoft.com/en-us/library/azure/ad/graph/api/functions-and-actions#checkMemberGroups
   */
  checkMemberGroups(id, groupIds) {
    return this._post({ groupIds }, `/users/${id}/checkMemberGroups`);
  }
  // timeout in ms
  checkMemberGroupsWithTimeout(id, groupId, timeout) {
    return __async(this, null, function* () {
      const result = yield this._post({ groupIds: [groupId] }, `/users/${id}/checkMemberGroups`, timeout);
      if (result.isSuccessful()) {
        const successData = result.getSuccessData();
        const arr = successData.value;
        return arr.includes(groupId);
      } else {
        return null;
      }
    });
  }
  getGroupByNickname(mailNickname) {
    return __async(this, null, function* () {
      const url = `/groups?$top=50&$filter=${encodeURIComponent(
        `mailNickname eq '${this.encodeOdataParam(mailNickname)}' and securityEnabled eq true`
      )}`;
      const response = yield this._get(url);
      const groups = response.getSuccessData();
      return _.first(groups.value);
    });
  }
  getGroupById(groupId) {
    return __async(this, null, function* () {
      const url = `/groups/${encodeURIComponent(groupId)}`;
      const response = yield this._get(url);
      if (response.statusCode === 404) {
        return null;
      }
      return response.getSuccessData();
    });
  }
  searchUsers(search, query) {
    return __async(this, null, function* () {
      const encodedSearch = this.encodeOdataParam(search);
      const filter = search && `accountEnabled eq true and (startswith(displayName,'${encodedSearch}') or startswith(userPrincipalName,'${encodedSearch}'))`;
      const url = `/users?$filter=${encodeURIComponent(filter)}${query}`;
      const response = yield this._get(url);
      const users = response.getSuccessData();
      return users;
    });
  }
  getUserInfo(id) {
    return __async(this, null, function* () {
      const url = `/users/${encodeURIComponent(id)}`;
      const response = yield this._get(url);
      if (response.statusCode === 404) {
        return null;
      }
      return response.isSuccessful() ? response.getSuccessData() : null;
    });
  }
  getUserImage(upn) {
    return __async(this, null, function* () {
      const encodedUpn = this.encodeOdataParam(upn);
      const url = `${this._baseUrl}/users/${encodedUpn}/photo/$value`;
      const xmlHTTP = new XMLHttpRequest();
      const token = yield MSALAuthService.getToken({
        tokenScope: resource + "/User.ReadBasic.All"
      });
      xmlHTTP.open("GET", url, true);
      xmlHTTP.setRequestHeader("Authorization", `Bearer ${token}`);
      xmlHTTP.responseType = "arraybuffer";
      const result = new Promise((resolve, reject) => {
        xmlHTTP.onerror = function(e) {
          resolve(RestResult.fromResult("", false));
        };
        xmlHTTP.onload = function(e) {
          if (xmlHTTP.status === 404) {
            resolve(RestResult.fromResult("", false));
          }
          const blb = new Blob([xmlHTTP.response], { type: "image/png" });
          const dataURL = URL.createObjectURL(blb);
          resolve(RestResult.fromResult(dataURL));
        };
        xmlHTTP.send();
      });
      return result;
    });
  }
  searchGroups(search, query) {
    return __async(this, null, function* () {
      const encodedSearch = this.encodeOdataParam(search);
      const groupFilter = search && `(startswith(displayName,'${encodedSearch}') or startswith(mailNickName,'${encodedSearch}'))`;
      const url = `/groups?$filter=${encodeURIComponent(groupFilter)}${query}`;
      const response = yield this._get(url);
      const groups = response.getSuccessData();
      return groups;
    });
  }
  getNextPage(currentPage) {
    return __async(this, null, function* () {
      const nextPage = currentPage["@odata.nextLink"];
      if (nextPage) {
        const response = yield this._get(nextPage);
        return response.getSuccessData();
      }
      throw new Error("Missing next page on currentPage");
    });
  }
}
__decorateClass([
  cache()
], GraphService.prototype, "searchUsers", 1);
__decorateClass([
  cache()
], GraphService.prototype, "getUserInfo", 1);
__decorateClass([
  cache()
], GraphService.prototype, "getUserImage", 1);
__decorateClass([
  cache()
], GraphService.prototype, "searchGroups", 1);
__decorateClass([
  cache()
], GraphService.prototype, "getNextPage", 1);
const GraphService$1 = new GraphService();
class GraphServiceBeta extends GraphService {
  constructor() {
    super("https://graph.microsoft.com/beta/");
  }
  searchApps(search, query) {
    return __async(this, null, function* () {
      const encodedSearch = this.encodeOdataParam(search);
      const searchId = isGuid(search) ? ` or (appId eq '${encodedSearch}') or (id eq '${encodedSearch}')` : "";
      const appFilter = search && `(startswith(displayName,'${encodedSearch}')${searchId})`;
      const url = `/applications?$filter=${encodeURIComponent(appFilter)}${query}`;
      const response = yield this._get(url);
      const apps = response.getSuccessData();
      return apps;
    });
  }
  getAppInfoByAppId(appId) {
    return __async(this, null, function* () {
      const url = `/applications?$filter=appId eq '${encodeURIComponent(appId)}'`;
      const response = yield this._get(url);
      if (response.statusCode === 404) {
        return null;
      }
      const data = response.getSuccessData();
      if (data.value.length !== 1) {
        return null;
      }
      return data.value[0];
    });
  }
  getAppImage(appId) {
    return __async(this, null, function* () {
      const appInfo = yield this.getAppInfoByAppId(appId);
      if (!appInfo)
        return null;
      const logoUrl = appInfo.info.logoUrl;
      if (!logoUrl)
        return null;
      const xmlHTTP = new XMLHttpRequest();
      xmlHTTP.open("GET", logoUrl, true);
      xmlHTTP.responseType = "arraybuffer";
      const result = new Promise((resolve, reject) => {
        xmlHTTP.onerror = function(e) {
          resolve(RestResult.fromResult("", false));
        };
        xmlHTTP.onload = function(e) {
          if (xmlHTTP.status === 404) {
            resolve(RestResult.fromResult("", false));
          }
          const blb = new Blob([xmlHTTP.response], { type: "image/png" });
          const dataURL = URL.createObjectURL(blb);
          resolve(RestResult.fromResult(dataURL));
        };
        xmlHTTP.send();
      });
      return result;
    });
  }
}
__decorateClass([
  cache()
], GraphServiceBeta.prototype, "searchApps", 1);
__decorateClass([
  cache()
], GraphServiceBeta.prototype, "getAppInfoByAppId", 1);
__decorateClass([
  cache()
], GraphServiceBeta.prototype, "getAppImage", 1);
const graphServiceBeta = new GraphServiceBeta();
export {
  GraphService,
  GraphServiceBeta,
  GraphService$1 as default,
  graphServiceBeta
};
//# sourceMappingURL=GraphService.js.map
